<script setup lang="ts">
import { useElementHover, useScroll } from '@vueuse/core';
import { computed, watch, useTemplateRef } from 'vue';

import { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import {
  SuggestionTypeSelector,
  SuggestionTypeSelectorSize,
  InfoIconPosition,
} from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import type {
  HotelIngredient,
  HotelIngredientFilters,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import type {
  AccommodationProductType,
  AmenitiesFilter,
  BoardBasisCode,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';
import { FULL_STAR_LIST } from '@libs/trip-planner-interfaces';
import type { AppMaskType, AppMultiSelectOption } from '@libs/ui';
import {
  AppButton,
  AppMask,
  arrivedStateToMaskType,
  AppMultiSelect,
  AppStarsSelector,
  AppCustomSelect,
  AppIconButton,
} from '@libs/ui';
import { isTouchableDevice } from '@libs/utils';

import { useHotelFiltersStore } from './store/hotelFiltersStore';
import HotelMealFilters from './components/hotelMealFilters/HotelMealFilters.vue';
import HotelAmenitiesFilters from './components/HotelAmenitiesFilters.vue';
import AccommodationTypeFilters from './components/AccommodationTypeFilters.vue';
import HotelNeighborhoodFilters from './components/HotelNeighborhoodFilters.vue';

const filtersStore = useFiltersStore();
const hotelFiltersStore = useHotelFiltersStore();

interface HotelFiltersButtonsProps {
  suggestionType: SuggestionType;
  boardBasisOptions: AppMultiSelectOption<BoardBasisCode>[] | undefined;
  amenitiesOptions: AppMultiSelectOption<AmenitiesFilter>[] | undefined;
  neighborhoodPolygonsOptions: AppMultiSelectOption<string>[] | undefined;
  accommodationTypeOptions:
    | AppMultiSelectOption<AccommodationProductType>[]
    | undefined;
  ingredient: HotelIngredient | OwnStayIngredient | null;
}
const props = defineProps<HotelFiltersButtonsProps>();
interface HotelFiltersButtonsEmits {
  (event: 'open-all-filters'): void;
  (event: 'open-why-we-suggest-modal'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
}

const emit = defineEmits<HotelFiltersButtonsEmits>();

const { t } = i18n.global;

const filtersContainerRef = useTemplateRef('filtersContainerRef');
const hotelFiltersButtonsRef = useTemplateRef('hotelFiltersButtonsRef');

const {
  arrivedState,
  x: xScroll,
  isScrolling,
} = useScroll(filtersContainerRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() =>
  arrivedStateToMaskType(arrivedState)
);

const isHotelFiltersButtonsHovered = useElementHover(hotelFiltersButtonsRef);
const selectedRatingLabel = computed<string | undefined>(() =>
  setSelectedRatingLabel(hotelFiltersStore.form.rating.stars[0] ?? 0)
);
const showScrollButtons = computed<boolean>(() =>
  getShowScrollButtons(isHotelFiltersButtonsHovered.value)
);

function getShowScrollButtons(isHovered: boolean): boolean {
  return isHovered && filtersContainerRef.value && hotelFiltersButtonsRef.value
    ? filtersContainerRef.value.scrollWidth >
        hotelFiltersButtonsRef.value.offsetWidth
    : false;
}

function setSelectedRatingLabel(
  selectedStars: StarsRatingFilter
): string | undefined {
  const maxRating = 5;
  if (
    selectedStars === 0 ||
    (props.ingredient &&
      (props.ingredient.filters as HotelIngredientFilters).starRating === 0)
  ) {
    return;
  }
  const ratingRange =
    selectedStars === maxRating
      ? selectedStars.toString()
      : `${selectedStars}-${maxRating}`;
  return `${ratingRange} ${t('filter.rating').toLocaleLowerCase()}`;
}

const shouldShowLeftArrow = computed<boolean>(
  () =>
    !isTouchableDevice() &&
    isHotelFiltersButtonsHovered.value &&
    !arrivedState.left &&
    showScrollButtons.value
);
const shouldShowRightArrow = computed<boolean>(
  () =>
    !isTouchableDevice() &&
    isHotelFiltersButtonsHovered.value &&
    !arrivedState.right &&
    showScrollButtons.value
);

function handleFilterUpdate(updateContext: HotelFilterUpdate): void {
  hotelFiltersStore.updateFormValue(updateContext);
  filtersStore.updateHotelFilter(
    updateContext,
    RemoveHotelSelectionFiltersOrigin.HotelList
  );
}

function clearMealFiltersSelection(): void {
  hotelFiltersStore.clearMealFilters();
  filtersStore.clearHotelMealFilters();
}

function clearAmenitiesFiltersSelection(): void {
  hotelFiltersStore.clearAmenitiesFilters();
  filtersStore.clearHotelAmenitiesFilters();
}

function clearAccommodationTypeFiltersSelection(): void {
  hotelFiltersStore.clearAccommodationTypeFilters();
  filtersStore.clearHotelAccommodationTypeFilters();
}

function clearNeighborhoodPolygonsFiltersSelection(): void {
  hotelFiltersStore.clearNeighborhoodPolygonsFilters();
  filtersStore.clearNeighborhoodPolygonsFilters();
}

watch(
  () => props.ingredient,
  () => {
    if (props.ingredient) {
      hotelFiltersStore.setFiltersValuesFromIngredient(props.ingredient);
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

function scrollLeft(): void {
  const factorToScroll = 0.15;
  if (filtersContainerRef.value) {
    const scrollValue = filtersContainerRef.value.offsetWidth * factorToScroll;
    xScroll.value -= scrollValue;
  }
}

function scrollRight(): void {
  const factorToScroll = 0.15;
  if (filtersContainerRef.value) {
    const scrollValue = filtersContainerRef.value.offsetWidth * factorToScroll;
    xScroll.value += scrollValue;
  }
}
</script>

<template>
  <div ref="hotelFiltersButtonsRef" class="hotel-filters-buttons">
    <div class="scroll-left-button">
      <AppIconButton
        v-if="shouldShowLeftArrow"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{ name: 'chevronLeft' }"
        :ariaLabel="$t('a11y.left_arrow_previous_dates')"
        class="scroll-left-button"
        @click="scrollLeft"
      />
    </div>

    <div class="scroll-right-button">
      <AppIconButton
        v-if="shouldShowRightArrow"
        type="secondary"
        state="enabled"
        size="small"
        :icon="{ name: 'chevronRight' }"
        :ariaLabel="$t('a11y.right_arrow_future_dates')"
        class="scroll-right-button"
        @click="scrollRight"
      />
    </div>
    <AppMask :type="maskType">
      <div ref="filtersContainerRef" class="hotel-selection-filters-buttons">
        <AppButton
          type="modern"
          state="enabled"
          size="small"
          :left-icon="{ name: 'tune' }"
          @click="emit('open-all-filters')"
        >
          {{ $t('filter.all_filters') }}
        </AppButton>
        <SuggestionTypeSelector
          font-variant="font-small"
          app-select-type="modern"
          icon-position="right"
          :suggestion-type="props.suggestionType"
          :show-info-icon="InfoIconPosition.None"
          :size="SuggestionTypeSelectorSize.Normal"
          :is-wrapper-scrolling="isScrolling"
          show-why-we-suggest-inside
          @suggestion-type-selected="emit('suggestion-type-selected', $event)"
          @show-info="emit('open-why-we-suggest-modal')"
        />
        <AppCustomSelect
          type="modern"
          :placeholder="t('filter.rating')"
          :is-wrapper-scrolling="isScrolling"
          :selected-placeholder="selectedRatingLabel"
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.RatingStars,
              value: 0,
            })
          "
        >
          <template #default="{ close }">
            <AppStarsSelector
              :options="FULL_STAR_LIST"
              :selected="hotelFiltersStore.form.rating.stars"
              @select="
                (handleFilterUpdate({
                  type: HotelFilter.RatingStars,
                  value: $event,
                }),
                close())
              "
            />
          </template>
        </AppCustomSelect>
        <HotelMealFilters
          v-if="props.boardBasisOptions"
          type="modern"
          :options="props.boardBasisOptions"
          :use-teleport="true"
          selected-placeholder-format="filter-name-X"
          :should-show-clear-button="true"
          :is-wrapper-scrolling="isScrolling"
          @meal-option-selected="handleFilterUpdate"
          @clear-selection="clearMealFiltersSelection"
        />
        <HotelAmenitiesFilters
          v-if="props.amenitiesOptions"
          :options="props.amenitiesOptions"
          :use-teleport="true"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          @amenity-option-selected="handleFilterUpdate"
          @clear-selection="clearAmenitiesFiltersSelection"
        />
        <AccommodationTypeFilters
          v-if="props.accommodationTypeOptions"
          :options="props.accommodationTypeOptions"
          :use-teleport="true"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          @accommodation-type-option-selected="handleFilterUpdate"
          @clear-selection="clearAccommodationTypeFiltersSelection"
        />
        <AppMultiSelect
          type="modern"
          :placeholder="t('filter.rules.free_cancellation')"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          :options="[
            {
              id: 1,
              label: t('filter.rules.free_cancellation'),
              selected: hotelFiltersStore.form.rules.freeCancellation,
            },
          ]"
          @select-single="
            handleFilterUpdate({
              type: HotelFilter.RuleFreeCancellation,
              value: !!$event.selected,
            })
          "
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.RuleFreeCancellation,
              value: false,
            })
          "
        />
        <AppMultiSelect
          type="modern"
          :placeholder="t('filter.travel_experience.family')"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          :options="[
            {
              id: 1,
              label: t('filter.travel_experience.family'),
              selected: hotelFiltersStore.form.experience.family,
            },
          ]"
          @select-single="
            handleFilterUpdate({
              type: HotelFilter.ExperienceFamily,
              value: !!$event.selected,
            })
          "
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.ExperienceFamily,
              value: false,
            })
          "
        />
        <AppMultiSelect
          type="modern"
          :placeholder="t('filter.travel_experience.business')"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          :options="[
            {
              id: 1,
              label: t('filter.travel_experience.business'),
              selected: hotelFiltersStore.form.experience.business,
            },
          ]"
          @select-single="
            handleFilterUpdate({
              type: HotelFilter.ExperienceBusiness,
              value: !!$event.selected,
            })
          "
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.ExperienceBusiness,
              value: false,
            })
          "
        />
        <AppMultiSelect
          type="modern"
          :placeholder="t('filter.travel_experience.pets')"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          :options="[
            {
              id: 1,
              label: t('filter.travel_experience.pets'),
              selected: hotelFiltersStore.form.experience.petsAllowed,
            },
          ]"
          @select-single="
            handleFilterUpdate({
              type: HotelFilter.ExperiencePetsAllowed,
              value: !!$event.selected,
            })
          "
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.ExperiencePetsAllowed,
              value: false,
            })
          "
        />
        <AppMultiSelect
          type="modern"
          :placeholder="t('filter.travel_experience.sustainable_property')"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          :options="[
            {
              id: 1,
              label: t('filter.travel_experience.sustainable_property'),
              selected:
                hotelFiltersStore.form.experience.greenSustainableProperty,
            },
          ]"
          @select-single="
            handleFilterUpdate({
              type: HotelFilter.ExperienceGreenSustainableProperty,
              value: !!$event.selected,
            })
          "
          @clear-selection="
            handleFilterUpdate({
              type: HotelFilter.ExperienceGreenSustainableProperty,
              value: false,
            })
          "
        />
        <HotelNeighborhoodFilters
          v-if="props.neighborhoodPolygonsOptions"
          :options="props.neighborhoodPolygonsOptions"
          :use-teleport="true"
          selected-placeholder-format="filter-name-X"
          should-show-clear-button
          :is-wrapper-scrolling="isScrolling"
          @neighborhood-option-selected="handleFilterUpdate"
          @clear-selection="clearNeighborhoodPolygonsFiltersSelection"
        />
      </div>
    </AppMask>
  </div>
</template>

<style scoped lang="scss">
.hotel-filters-buttons {
  position: relative;
  .scroll-left-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    z-index: 1;
  }

  .scroll-right-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: var(--spacing-8);
    z-index: 1;
  }
  .hotel-selection-filters-buttons {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    gap: var(--spacing-8);
    padding-right: var(--spacing-16);
    overflow-x: auto;
    overflow-y: hidden;

    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer/Edge */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari */
    }
  }
}
</style>
